import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Loader2, Crown } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSearchParams } from "react-router-dom";

const inputCls = "w-full bg-black/55 border border-white/25 px-5 py-4 text-sm text-[#FDFBF7] placeholder-[#8f887a] rounded-xl focus:outline-none focus:border-[#D4AF37] transition-colors";

export default function GoldenTicket() {
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get("code") || "");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [claimed, setClaimed] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim() || !name.trim() || !email.includes("@")) {
      toast.error("Enter your name, email, and golden ticket code.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/golden-tickets/claim", { code: code.trim(), name: name.trim(), email: email.trim(), phone: phone.trim() });
      setClaimed(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not claim that ticket — try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#070202] flex items-center justify-center px-5 py-14" data-testid="golden-ticket-page">
      <div aria-hidden="true" className="absolute inset-0 bg-cover bg-[position:right_top] opacity-40"
        style={{ backgroundImage: "url(/ep3-bg.jpg?v=11)", filter: "brightness(0.5) saturate(1.05)" }} />
      <div aria-hidden="true" className="absolute inset-0"
        style={{ background: "linear-gradient(180deg, rgba(7,2,2,0.55) 0%, rgba(7,2,2,0.75) 100%)" }} />

      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 w-full max-w-md text-center">
        <img src="/logo-jayatk.png" alt="Jay Atk" className="h-12 w-auto mx-auto mb-6" />
        <div className="flex items-center justify-center gap-3 text-[#D4AF37] text-[11px] uppercase tracking-[0.35em]">
          <Crown className="w-4 h-4" /> Golden Ticket · Chocolate City
        </div>

        {claimed ? (
          <div className="mt-8 border border-[#D4AF37]/50 bg-black/70 backdrop-blur-xl rounded-2xl px-8 py-10" data-testid="golden-claim-success"
            style={{ boxShadow: "0 0 32px rgba(212,175,55,0.18)" }}>
            <Check className="w-8 h-8 mx-auto mb-4 text-[#D4AF37]" />
            <h1 className="font-didone font-bold text-3xl text-[#F4E7C0]">You're verified.</h1>
            <p className="text-sm text-[#A19D94] mt-3 leading-relaxed">
              Jay Atk Events will connect you with the nail professional to schedule your complimentary
              manicure and pedicure — keep an eye on your inbox. See you at Chocolate City.
            </p>
          </div>
        ) : (
          <div className="mt-8 border border-[#D4AF37]/40 bg-black/70 backdrop-blur-xl rounded-2xl px-7 py-9 text-left" data-testid="golden-claim-card">
            <h1 className="font-didone font-bold text-3xl text-[#F4E7C0] text-center">Claim your prize</h1>
            <p className="text-sm text-[#A19D94] mt-3 text-center leading-relaxed">
              Enter your contact info and the unique code from your winner email. Once verified, you'll get the
              nail professional's details to schedule your appointment.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-3" data-testid="golden-claim-form">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className={inputCls} data-testid="golden-name" />
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email address" className={inputCls} data-testid="golden-email" />
              <input value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" placeholder="Mobile number" className={inputCls} data-testid="golden-phone" />
              <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="GOLDEN TICKET CODE"
                className={`${inputCls} text-center tracking-[0.3em] uppercase border-[#D4AF37]/50`} data-testid="golden-code" />
              <button type="submit" disabled={busy} data-testid="golden-submit"
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl border-2 border-[#D4AF37] bg-[#D4AF37] text-[#1a0a05] px-8 py-4 text-sm font-bold uppercase tracking-[0.3em] transition-all duration-300 hover:bg-transparent hover:text-[#D4AF37] disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Claim My Golden Ticket"}
              </button>
            </form>
          </div>
        )}
        <a href="/" className="inline-block mt-8 text-[11px] uppercase tracking-[0.25em] text-[#8C7324] hover:text-[#D4AF37] transition-colors" data-testid="golden-back-home">
          ← Back to jayatk.com
        </a>
      </motion.div>
    </div>
  );
}
